import { computed, mergeProps, reactive, toValue, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { camelCase, get } from 'lodash';

import {
  AssetContainerEventType,
  formatDateTimeWithMilitarySupport,
  getAssetContainerStatus,
  LuxonDateTimeFormats
} from '@nova/core';
import {
  getAssetContainerStateIcon,
  getAssetContainerStatusColor,
  getAssetContainerStatusIcon
} from '@satellite/composables/yardAssetPresenterV2Shared';
import { nestedAppointmentPresenterDataProps } from '@satellite/composables/presenterV2NestedAppointmentProps';
import { nestedAssetVisitPresenterDataProps } from '@satellite/composables/presenterV2NestedAssetVisitProps';
import { nestedCompanyPresenterDataProps } from '@satellite/composables/presenterV2NestedCompanyProps';
import { nestedSpotPresenterDataProps } from '@satellite/composables/presenterV2NestedSpotProps';
import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { reactiveOption } from '@satellite/composables/presenterV2ReactiveOption';
import { toPresenterPlaceholder } from '@satellite/composables/presenterV2Placeholder';

import type { ComputedRef, Ref } from 'vue';
import type { ComposerTranslation } from 'vue-i18n';
import type { Maybe } from '@nova/core';
import type {
  PresenterV2DisplayConfigOptions,
  PresenterV2DisplayOverrideProps,
  PresenterV2DisplaySize
} from '@satellite/composables/presenterV2DisplayOptions';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterV2Placeholder';
import type { YardSpotDetails } from '@satellite/composables/useYardSpotPresenterV2';
import type { YardAssetDetails } from './yardAssetPresenterV2Shared';

export type YardAssetDisplayField =
  | 'arrivedTime'
  | 'code'
  | 'dropoffCarrierCompany'
  | 'owner'
  | 'pickupCarrierCompany'
  | 'spot'
  | 'state'
  | 'stateChip'
  | 'stateIcon'
  | 'status'
  | 'statusChip'
  | 'statusIcon'
  | 'tags'
  | 'type'
  | 'typeChip'
  | 'typeIcon';

export interface UseYardAssetDisplayV2Options extends PresenterV2DisplayConfigOptions {
  codeProps?: PresenterV2DisplayOverrideProps;
  arrivedTimeProps?: PresenterV2DisplayOverrideProps;
  stateProps?: PresenterV2DisplayOverrideProps;
  statusProps?: PresenterV2DisplayOverrideProps;
  statusIconProps?: PresenterV2DisplayOverrideProps;
  typeChipProps?: PresenterV2DisplayOverrideProps;
  typeIconProps?: PresenterV2DisplayOverrideProps;
  typeProps?: PresenterV2DisplayOverrideProps;
  spotPresenterProps?: PresenterV2DisplayOverrideProps;
  stateIconProps?: PresenterV2DisplayOverrideProps;
  tagsContainerProps?: PresenterV2DisplayOverrideProps;
}

type DisplaySize = PresenterV2DisplaySize;

const OUTLINED_STATUS_CHIP_STATUSES = [
  AssetContainerEventType.Arrived,
  AssetContainerEventType.Loading,
  AssetContainerEventType.Unloading
];

function chipSizeForDisplay(size: DisplaySize) {
  return size === 'small' ? 'x-small' : 'small';
}

function resolveSpotPlaceholderFromAsset(
  asset: YardAssetDetails | null
): PresenterPlaceholderData<YardSpotDetails> | null {
  if (!asset) {
    return null;
  }

  const assignment = asset.spotAssignments?.[0] ?? asset.assetVisit?.spotAssignments?.[0];
  const spotId = assignment?.spotId ?? null;

  if (!spotId) {
    return null;
  }

  const spot = (assignment as { spot?: YardSpotDetails } | undefined)?.spot;

  return toPresenterPlaceholder<YardSpotDetails>({
    id: spotId,
    code: spot?.code ?? '',
    type: spot?.type ?? '',
    spotAreaId: spot?.spotAreaId ?? ''
  });
}

function chipTextTypographyClass(size: DisplaySize) {
  return {
    'typography-chip-sm': size === 'small',
    'typography-caption-bold': ['regular', 'large'].includes(size)
  };
}

function translateAssetContainerType(t: ComposerTranslation, type: Maybe<string>) {
  if (!type) {
    return null;
  }

  return t(`core.yard.assetContainerTypes.${camelCase(type)}`);
}

function formatAssetDateTime(dateTime: string, warehouseTimezone: Maybe<string>) {
  return formatDateTimeWithMilitarySupport(
    dateTime,
    warehouseTimezone,
    LuxonDateTimeFormats.ShortMonthYearDayTimeAMPM,
    false,
    LuxonDateTimeFormats.ShortMonthYearDayTime24
  );
}

function resolveSpotIcon(hasAssignment: boolean) {
  return hasAssignment ? 'mdi-map-marker-outline' : 'mdi-map-marker-off-outline';
}

/** Resolve dropoff (arrived) carrier name from container data (no store). */
export function getDropoffCarrierName(assetContainer: YardAssetDetails | null) {
  if (!assetContainer) {
    return null;
  }

  const appointmentId = assetContainer.dropAppointment?.id ?? assetContainer.dropAppointmentId;
  if (appointmentId) {
    return {
      appointmentId,
      appointment: assetContainer.dropAppointment ?? null,
      assetVisitId: null,
      assetVisit: null
    };
  }

  const assetContainerEvents = Array.isArray(assetContainer.assetContainerEvents)
    ? assetContainer.assetContainerEvents
    : [];
  const arrivedEvent = assetContainerEvents.find(
    event => event.eventType === AssetContainerEventType.Arrived
  );

  if (!arrivedEvent) {
    return null;
  }

  if (arrivedEvent.assetVisitId || arrivedEvent.assetVisit?.id) {
    return {
      appointmentId: null,
      appointment: null,
      assetVisitId: arrivedEvent.assetVisitId ?? arrivedEvent.assetVisit?.id,
      assetVisit: arrivedEvent.assetVisit ?? null
    };
  }

  const assetVisitId = get(arrivedEvent, 'assetVisitId');
  if (assetVisitId) {
    return {
      appointmentId: null,
      appointment: null,
      assetVisitId,
      assetVisit: null
    };
  }

  return null;
}

export function getPickupCarrierName(assetContainer: YardAssetDetails | null) {
  if (!assetContainer) {
    return null;
  }

  const appointmentId = assetContainer.pickupAppointment?.id ?? assetContainer.pickupAppointmentId;
  if (appointmentId) {
    return {
      appointmentId,
      appointment: assetContainer.pickupAppointment ?? null,
      assetVisitId: null,
      assetVisit: null
    };
  }

  return null;
}

export function useYardAssetDisplayV2(
  asset: Ref<YardAssetDetails | null> | ComputedRef<YardAssetDetails | null>,
  options: UseYardAssetDisplayV2Options = {}
) {
  const { t } = useI18n();

  const displaySize = reactiveOption(options.size, 'regular');
  const displayMode = reactiveOption(options.mode, EntityPresenterV2Mode.Live);
  const effectiveTimezone = computed(() => toValue(options.warehouseTimezone) ?? null);
  const isFetchingValue = computed(() => (options.isFetching ? unref(options.isFetching) : false));

  const code = computed(() => asset.value?.code ?? null);
  const typeRaw = computed(() => asset.value?.type ?? null);
  const typeLabel = computed(() => translateAssetContainerType(t, typeRaw.value));
  const arrivedTime = computed(() => asset.value?.createDateTime ?? null);
  const arrivedTimeFormatted = computed(() =>
    arrivedTime.value ? formatAssetDateTime(arrivedTime.value, effectiveTimezone.value) : null
  );
  const owner = computed(() => asset.value?.company ?? null);
  const state = computed(() => (asset.value?.state ? String(asset.value.state) : null));
  const tags = computed(() => asset.value?.tags ?? null);

  const status = computed(() => {
    const events = asset.value?.assetContainerEvents ?? [];
    if (!Array.isArray(events) || !events.length) {
      return null;
    }

    return getAssetContainerStatus(events as Parameters<typeof getAssetContainerStatus>[0]) ?? null;
  });

  const statusIcon = computed(() => getAssetContainerStatusIcon(asset.value));
  const statusColor = computed(
    () => getAssetContainerStatusColor(asset.value) ?? 'text--color-accent-60'
  );
  const statusChipVariant = computed(() =>
    OUTLINED_STATUS_CHIP_STATUSES.includes(status.value as AssetContainerEventType)
      ? 'outlined'
      : 'tonal'
  );
  const stateIcon = computed(() => getAssetContainerStateIcon(asset.value));

  const spotId = computed(
    () =>
      asset.value?.spotAssignments?.[0]?.spotId ??
      asset.value?.assetVisit?.spotAssignments?.[0]?.spotId ??
      null
  );
  const hasAssignment = computed(() => Boolean(spotId.value));
  const spotIcon = computed(() => resolveSpotIcon(hasAssignment.value));

  const dropoffCarrier = computed(() => getDropoffCarrierName(asset.value));
  const pickupCarrier = computed(() => getPickupCarrierName(asset.value));

  const ownerPresenterProps = computed(() =>
    nestedCompanyPresenterDataProps(displayMode.value, { company: owner.value })
  );
  const spotPlaceholder = computed(() => resolveSpotPlaceholderFromAsset(asset.value));
  const spotPresenterPropsMerged = computed(() =>
    mergeProps(
      nestedSpotPresenterDataProps(displayMode.value, {
        spot: spotPlaceholder.value,
        spotArea: null
      }),
      toValue(options.spotPresenterProps) ?? {}
    )
  );
  const dropoffAppointmentPresenterProps = computed(() =>
    nestedAppointmentPresenterDataProps(displayMode.value, {
      appointment: dropoffCarrier.value?.appointment ?? null
    })
  );
  const pickupAppointmentPresenterProps = computed(() =>
    nestedAppointmentPresenterDataProps(displayMode.value, {
      appointment: pickupCarrier.value?.appointment ?? null
    })
  );
  const dropoffAssetVisitPresenterProps = computed(() =>
    nestedAssetVisitPresenterDataProps(displayMode.value, {
      assetVisit: dropoffCarrier.value?.assetVisit ?? null
    })
  );
  const pickupAssetVisitPresenterProps = computed(() =>
    nestedAssetVisitPresenterDataProps(displayMode.value, {
      assetVisit: pickupCarrier.value?.assetVisit ?? null
    })
  );

  const codeAttrs = computed(() =>
    mergeProps(
      {
        class: {
          'typography-body-sm-bold': displaySize.value === 'small',
          'typography-body-md-bold': displaySize.value === 'regular',
          'text-capitalize': true
        },
        'data-testid': 'yard-asset-presenter-asset-code'
      },
      toValue(options.codeProps) ?? {}
    )
  );

  const typeAttrs = computed(() =>
    mergeProps(
      { class: 'text-capitalize', 'data-testid': 'yard-asset-presenter-asset-type' },
      toValue(options.typeProps) ?? {}
    )
  );

  const typeChipAttrs = computed(() =>
    mergeProps(
      {
        variant: 'tonal',
        size: chipSizeForDisplay(displaySize.value),
        'data-testid': 'yard-asset-presenter-asset-type-chip'
      },
      toValue(options.typeChipProps) ?? {}
    )
  );

  const typeChipTextAttrs = computed(() => ({
    class: {
      ...chipTextTypographyClass(displaySize.value),
      'text-capitalize': true,
      'text--color-text-neutral-90': true
    },
    'data-testid': 'yard-asset-presenter-asset-type-chip-text'
  }));

  const typeIconAttrs = computed(() =>
    mergeProps(
      {
        size: 20,
        icon: 'mdi-truck-trailer',
        'data-testid': 'yard-asset-presenter-asset-type-icon'
      },
      toValue(options.typeIconProps) ?? {}
    )
  );

  const arrivedTimeAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-arrived-time' },
      toValue(options.arrivedTimeProps) ?? {}
    )
  );

  const statusAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-asset-status' },
      toValue(options.statusProps) ?? {}
    )
  );

  const statusChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    ripple: true,
    variant: statusChipVariant.value,
    prependIcon: statusIcon.value,
    color: statusColor.value,
    'data-testid': 'yard-asset-presenter-asset-status-chip'
  }));

  const statusChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-asset-status-chip-text'
  }));

  const statusIconAttrs = computed(() =>
    mergeProps(
      {
        size: '1em',
        icon: statusIcon.value,
        color: statusColor.value,
        'data-testid': 'yard-asset-presenter-asset-status-icon'
      },
      toValue(options.statusIconProps) ?? {}
    )
  );

  const stateAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-asset-state' },
      toValue(options.stateProps) ?? {}
    )
  );

  const stateChipAttrs = computed(() => ({
    size: chipSizeForDisplay(displaySize.value),
    ripple: true,
    variant: 'outlined',
    prependIcon: stateIcon.value,
    'data-testid': 'yard-asset-presenter-asset-state-chip'
  }));

  const stateChipTextAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-asset-state-chip-text'
  }));

  const tagChipAttrs = computed(() => ({
    class: chipTextTypographyClass(displaySize.value),
    'data-testid': 'yard-asset-presenter-tag-chip'
  }));

  const tagsContainerAttrs = computed(() =>
    mergeProps(
      {
        class: 'd-flex flex-row gap-1 flex-wrap',
        'data-testid': 'yard-asset-presenter-tags'
      },
      toValue(options.tagsContainerProps) ?? {}
    )
  );

  const stateIconAttrs = computed(() =>
    mergeProps(
      { 'data-testid': 'yard-asset-presenter-state-icon' },
      toValue(options.stateIconProps) ?? {}
    )
  );

  // Reactive shell so nested computed refs unwrap in templates and slot props when passed as a prop.
  return reactive({
    asset,
    mode: displayMode,
    size: displaySize,
    isFetching: isFetchingValue,
    code,
    typeRaw,
    typeLabel,
    arrivedTime,
    arrivedTimeFormatted,
    owner,
    status,
    state,
    tags,
    statusIcon,
    statusColor,
    stateIcon,
    spotId,
    dropoffCarrier,
    pickupCarrier,
    hasAssignment,
    statusChipVariant,
    spotIcon,
    codeAttrs,
    typeAttrs,
    typeChipAttrs,
    typeChipTextAttrs,
    typeIconAttrs,
    arrivedTimeAttrs,
    statusAttrs,
    statusChipAttrs,
    statusChipTextAttrs,
    statusIconAttrs,
    stateAttrs,
    stateChipAttrs,
    stateChipTextAttrs,
    tagChipAttrs,
    tagsContainerAttrs,
    stateIconAttrs,
    ownerPresenterProps,
    spotPresenterProps: spotPresenterPropsMerged,
    dropoffAppointmentPresenterProps,
    pickupAppointmentPresenterProps,
    dropoffAssetVisitPresenterProps,
    pickupAssetVisitPresenterProps
  });
}

export type YardAssetDisplayV2Data = ReturnType<typeof useYardAssetDisplayV2>;

/** @deprecated Use {@link YardAssetDisplayV2Data} */
export type YardAssetDisplayV2State = YardAssetDisplayV2Data;
